import { type PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { ExperienceLevel, Goals, HeardAboutUs, LeadType } from "@modules/boxLead/entities/boxLeadTypes"
import { type BoxLeadDto } from "@modules/boxLead/gateway/boxLeadDto"
import { BoxStaffLightEntity } from "@modules/boxStaff/entities/boxStaffLightEntity"
import { word } from "@modules/core/utils/i18n"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import dayjs from "dayjs"

export type BoxLeadState = Omit<
  BoxLeadEntity,
  | "getWordingType"
  | "getUserName"
  | "getWordingExperienceLevel"
  | "getWordingGoals"
  | "getWordingHeardAboutUs"
  | "getColorFirstContact"
  | "getWordingBestTimeToContact"
>

export class BoxLeadEntity {
  id: string
  boxMemberLight: {
    boxMemberId: string
    user: UserEntity
  } | null
  boxId: string
  user: UserEntity | null
  experienceLevel: ExperienceLevel | null
  goals: Goals[] | null
  hearAboutUs: HeardAboutUs | null
  hearAboutUsSpecify: string | null
  bestTimeToContact: string | null
  madeByStaff: BoxStaffLightEntity | null
  firstname: string | null
  lastname: string | null
  email: string | null
  phoneNumber: PhoneNumberWithPrefix | null
  note: string | null
  firstContact: Date
  leadType: LeadType | null
  isHot: boolean | null

  private constructor(boxLead: BoxLeadState) {
    this.id = boxLead.id
    this.boxMemberLight = boxLead.boxMemberLight
    this.boxId = boxLead.boxId
    this.user = boxLead.user
    this.experienceLevel = boxLead.experienceLevel
    this.goals = boxLead.goals
    this.hearAboutUs = boxLead.hearAboutUs
    this.hearAboutUsSpecify = boxLead.hearAboutUsSpecify
    this.bestTimeToContact = boxLead.bestTimeToContact
    this.madeByStaff = boxLead.madeByStaff
    this.firstname = boxLead.firstname
    this.lastname = boxLead.lastname
    this.email = boxLead.email
    this.phoneNumber = boxLead.phoneNumber
    this.note = boxLead.note
    this.firstContact = boxLead.firstContact
    this.leadType = boxLead.leadType
    this.isHot = boxLead.isHot
  }

  static fromDto(boxLeadDto: BoxLeadDto): BoxLeadEntity {
    return new BoxLeadEntity({
      id: boxLeadDto.id,
      boxMemberLight: boxLeadDto.boxMemberLight
        ? {
            boxMemberId: boxLeadDto.boxMemberLight.boxMemberId,
            user: UserEntity.fromDto(boxLeadDto.boxMemberLight.user),
          }
        : null,
      boxId: boxLeadDto.boxId,
      user: boxLeadDto.user ? UserEntity.fromDto(boxLeadDto.user) : null,
      experienceLevel: boxLeadDto.experienceLevel,
      goals: boxLeadDto.goals,
      hearAboutUs: boxLeadDto.hearAboutUs,
      hearAboutUsSpecify: boxLeadDto.hearAboutUsSpecify,
      bestTimeToContact: boxLeadDto.bestTimeToContact,
      madeByStaff: boxLeadDto.madeByStaff ? BoxStaffLightEntity.fromDto(boxLeadDto.madeByStaff) : null,
      firstname: boxLeadDto.firstname,
      lastname: boxLeadDto.lastname,
      email: boxLeadDto.email,
      phoneNumber: boxLeadDto.phoneNumber,
      note: boxLeadDto.note,
      firstContact: new Date(boxLeadDto.firstContact),
      leadType: boxLeadDto.leadType,
      isHot: boxLeadDto.isHot,
    })
  }

  getUserName(): string {
    if (!this.user?.username) {
      return word("boxLeadEntity.wording.userName.outsider")
    }
    return "@" + this.user.username
  }

  getWordingType(): string {
    switch (this.leadType) {
      case LeadType.CONTACTED_FROM_SHOWCASE:
        return word("boxLeadEntity.wording.leadType.contactedFromShowcase")
      case LeadType.INTERRUPTED_PURCHASE:
        return word("boxLeadEntity.wording.leadType.interruptedPurchase")
      case LeadType.MANUAL_ADDITION:
        return word("boxLeadEntity.wording.leadType.manualAddition")
      case LeadType.TRIAL_SESSION:
        return word("boxLeadEntity.wording.leadType.trialSession")
      default:
        return word("boxLeadEntity.wording.leadType.unknown")
    }
  }

  getWordingExperienceLevel(): string {
    switch (this.experienceLevel) {
      case ExperienceLevel.LESS_THAN_SIX_MONTHS:
        return word("boxLeadEntity.wording.experienceLevel.lessThanSixMonths")
      case ExperienceLevel.LESS_THAN_A_YEAR:
        return word("boxLeadEntity.wording.experienceLevel.lessThanAYear")
      case ExperienceLevel.MORE_THAN_A_YEAR:
        return word("boxLeadEntity.wording.experienceLevel.moreThanAYear")
      case ExperienceLevel.NONE:
        return word("boxLeadEntity.wording.experienceLevel.none")
      default:
        return word("boxLeadEntity.wording.experienceLevel.unknown")
    }
  }

  getWordingGoals(): string {
    if (!this.goals || this.goals.length === 0) {
      return word("boxLeadEntity.wording.goals.unknown")
    }

    return this.goals
      .map((goal) => {
        switch (goal) {
          case Goals.GETTING_BACK_INTO_SPORT:
            return word("boxLeadEntity.wording.goals.gettingBackIntoSport")
          case Goals.LOSE_WEIGHT:
            return word("boxLeadEntity.wording.goals.loseWeight")
          case Goals.OTHER:
            return word("boxLeadEntity.wording.goals.other")
          case Goals.PERFORMANCE:
            return word("boxLeadEntity.wording.goals.performance")
          case Goals.RETURN_TO_EXERCISE_AFTER_AN_INJURY:
            return word("boxLeadEntity.wording.goals.returnToExerciseAfterAnInjury")
          default:
            return ""
        }
      })
      .join(", ")
  }

  getWordingHeardAboutUs(): string {
    switch (this.hearAboutUs) {
      case HeardAboutUs.SOCIAL_NETWORK:
        return word("boxLeadEntity.wording.hearAboutUs.socialNetwork")
      case HeardAboutUs.INFLUENCERS_AMBASSADORS:
        return word("boxLeadEntity.wording.hearAboutUs.influencers")
      case HeardAboutUs.LOCAL_ADVERTISING:
        return word("boxLeadEntity.wording.hearAboutUs.localAdvertising")
      case HeardAboutUs.ONLINE_SEARCH:
        return word("boxLeadEntity.wording.hearAboutUs.onlineSearch")
      case HeardAboutUs.OTHER:
        return this.hearAboutUsSpecify ?? word("boxLeadEntity.wording.hearAboutUs.other")
      case HeardAboutUs.PASSED_BY:
        return word("boxLeadEntity.wording.hearAboutUs.passedBy")
      case HeardAboutUs.SPORTS_EVENTS_COMPETITIONS:
        return word("boxLeadEntity.wording.hearAboutUs.events")
      case HeardAboutUs.WORD_OF_MOUTH:
        return word("boxLeadEntity.wording.hearAboutUs.wordOfMouth")
      default:
        return word("boxLeadEntity.wording.hearAboutUs.unknown")
    }
  }

  getWordingBestTimeToContact(): string {
    if (!this.bestTimeToContact) {
      return word("boxLeadEntity.wording.bestTimeToContact.unknown")
    }
    return this.bestTimeToContact
  }

  getColorFirstContact(): string {
    const now = new Date()
    const diffDays = dayjs(now).diff(dayjs(this.firstContact), "day", true)

    if (diffDays <= 2) {
      return "text-textSuccess"
    } else if (diffDays <= 5) {
      return "text-custom-yellow-500"
    } else {
      return "text-textError"
    }
  }
}
